import {
  LGM_TEAL_DARK,
  LGM_TEAL_TINT,
  LGM_PURPLE_DARK,
  COLOR_INFO_TEXT,
  COLOR_SUCCESS_TEXT,
} from './colors';

/**
 * Shared color tokens for the product modal's four source panels
 * (Orange Book, DMF, CAS Registry, Manufacturer) — Requirement 6. Centralized
 * here so a future palette change touches one place instead of four
 * per-component inline hex values.
 */
export interface SourcePanelPalette {
  border: string;
  background: string;
  headerBackground: string;
  headerText: string;
  badgeBackground: string;
  badgeText: string;
}

/** Orange Book — existing theme, unchanged (Requirement 6 AC1). */
export const ORANGE_BOOK_PANEL_COLORS: SourcePanelPalette = {
  border: '#fed7aa',
  background: '#fff7ed',
  headerBackground: '#ffedd5',
  headerText: '#9a3412',
  badgeBackground: '#fdba74',
  badgeText: '#c2410c',
};

/** DMF — teal/cyan, distinct from Orange Book's identical-until-now orange (Requirement 6 AC2). */
export const DMF_PANEL_COLORS: SourcePanelPalette = {
  border: LGM_TEAL_TINT,
  background: '#ecf8fa',
  headerBackground: '#d3eef2',
  headerText: LGM_TEAL_DARK,
  badgeBackground: LGM_TEAL_TINT,
  badgeText: LGM_TEAL_DARK,
};

/** CAS Registry — existing blue, promoted from thin inline chrome to full panel treatment (Requirement 6 AC3). */
export const CAS_PANEL_COLORS: SourcePanelPalette = {
  border: '#93c5fd',
  background: '#eff6ff',
  headerBackground: '#dbeafe',
  headerText: COLOR_INFO_TEXT,
  badgeBackground: '#93c5fd',
  badgeText: COLOR_INFO_TEXT,
};

/** Manufacturer — new panel wrapper around ListBuilder, previously unstyled (Requirement 6 AC4). */
export const MANUFACTURER_PANEL_COLORS: SourcePanelPalette = {
  border: '#ddd6fe',
  background: '#f5f3ff',
  headerBackground: '#ede9fe',
  headerText: LGM_PURPLE_DARK,
  badgeBackground: '#ddd6fe',
  badgeText: LGM_PURPLE_DARK,
};

/** NDC — new panel for the External Sources redesign, previously no NDC-specific panel existed. */
export const NDC_PANEL_COLORS: SourcePanelPalette = {
  border: '#bbf7d0',
  background: '#f0fdf4',
  headerBackground: '#dcfce7',
  headerText: COLOR_SUCCESS_TEXT,
  badgeBackground: '#bbf7d0',
  badgeText: COLOR_SUCCESS_TEXT,
};

/**
 * Manufacturer modal Details tab source panels (issue #653, Task 16 QA
 * follow-up) — DECRS/GLEIF/SAP AML/Salesforce/CEP each get their own hue,
 * distinct from every product-panel color above and from each other.
 * USDMF/Orange Book/NDC reuse the product palettes above (DMF/Orange
 * Book/NDC respectively) since they're the same real-world source.
 */

/** DECRS — amber. */
export const DECRS_PANEL_COLORS: SourcePanelPalette = {
  border: '#fde68a',
  background: '#fffbeb',
  headerBackground: '#fef3c7',
  headerText: '#92400e',
  badgeBackground: '#fde68a',
  badgeText: '#92400e',
};

/** GLEIF — indigo. */
export const GLEIF_PANEL_COLORS: SourcePanelPalette = {
  border: '#c7d2fe',
  background: '#eef2ff',
  headerBackground: '#e0e7ff',
  headerText: '#3730a3',
  badgeBackground: '#c7d2fe',
  badgeText: '#3730a3',
};

/** SAP AML — rose. */
export const SAP_AML_PANEL_COLORS: SourcePanelPalette = {
  border: '#fecdd3',
  background: '#fff1f2',
  headerBackground: '#ffe4e6',
  headerText: '#9f1239',
  badgeBackground: '#fecdd3',
  badgeText: '#9f1239',
};

/** Salesforce — sky blue. */
export const SALESFORCE_PANEL_COLORS: SourcePanelPalette = {
  border: '#bae6fd',
  background: '#f0f9ff',
  headerBackground: '#e0f2fe',
  headerText: '#075985',
  badgeBackground: '#bae6fd',
  badgeText: '#075985',
};

/** CEP — slate. */
export const CEP_PANEL_COLORS: SourcePanelPalette = {
  border: '#cbd5e1',
  background: '#f8fafc',
  headerBackground: '#e2e8f0',
  headerText: '#334155',
  badgeBackground: '#cbd5e1',
  badgeText: '#334155',
};
